/**
 * Los iconos del HUD, dibujados a mano en SVG.
 *
 * Son inline y no una librería de iconos a propósito: son nueve, pesan menos que el
 * `import` que haría falta para traerlos, y heredan el color del texto — que es lo
 * que hace que la misma ficha del dock sirva en crema sobre azul y en dorado.
 *
 * Todos comparten caja de 24x24 y trazo de 2, así que se ven del mismo peso al
 * ponerlos juntos aunque el dibujo sea distinto.
 */

interface Props {
  /** Lado del cuadro, en px. El trazo no escala: a tamaños grandes se ve más fino. */
  size?: number;
}

function frame(size: number) {
  return {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
    focusable: false,
  };
}

/** Crédito. El aro interior es lo que la lee como moneda y no como círculo. */
export function IconMoneda({ size = 14 }: Props) {
  return (
    <svg {...frame(size)}>
      <circle cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="12" r="4" />
    </svg>
  );
}

/** Birrete: el sello ESPOL del letrero de sala. */
export function IconEscudo({ size = 20 }: Props) {
  return (
    <svg {...frame(size)}>
      <path d="M2.5 9.5 12 5.2l9.5 4.3L12 13.8z" />
      <path d="M6.8 11.6V16c0 1.4 2.3 2.8 5.2 2.8s5.2-1.4 5.2-2.8v-4.4" />
    </svg>
  );
}

export function IconJugar({ size = 26 }: Props) {
  return (
    <svg {...frame(size)}>
      <rect x="2.4" y="7.6" width="19.2" height="10.8" rx="5.4" />
      <path d="M7 11.4v3.4M5.3 13.1h3.4" />
      <circle cx="16.2" cy="12.3" r="1.15" />
      <circle cx="18.4" cy="14.8" r="1.15" />
    </svg>
  );
}

export function IconMochila({ size = 26 }: Props) {
  return (
    <svg {...frame(size)}>
      <path d="M5.6 10.4a6.4 6.4 0 0 1 12.8 0v7.4a2.4 2.4 0 0 1-2.4 2.4H8a2.4 2.4 0 0 1-2.4-2.4z" />
      <path d="M9.4 9.6V7.4a2.6 2.6 0 0 1 5.2 0v2.2" />
      <path d="M9.2 14h5.6v3.6H9.2z" />
    </svg>
  );
}

export function IconAmigos({ size = 26 }: Props) {
  return (
    <svg {...frame(size)}>
      <circle cx="12" cy="8.6" r="3.1" />
      <path d="M7.4 19.4a4.6 4.6 0 0 1 9.2 0" />
      <circle cx="4.9" cy="11" r="2.1" />
      <circle cx="19.1" cy="11" r="2.1" />
      <path d="M2.4 18.2a3.3 3.3 0 0 1 3-2.9M21.6 18.2a3.3 3.3 0 0 0-3-2.9" />
    </svg>
  );
}

/**
 * La cartelera de la sala: un tablero con una fila por persona.
 *
 * El punto y la raya de cada fila son el mismo par que dibuja la cartelera de verdad
 * —color del avatar y nombre—, que es lo que la distingue de `IconAmigos`: ésta es
 * la lista de quién está aquí, aquélla la gente que conoces.
 */
export function IconCartelera({ size = 26 }: Props) {
  return (
    <svg {...frame(size)}>
      <rect x="3.4" y="4.4" width="17.2" height="15.2" rx="2.4" />
      <circle cx="8" cy="9.6" r="1.15" />
      <circle cx="8" cy="14.4" r="1.15" />
      <path d="M11.6 9.6h5M11.6 14.4h5" />
    </svg>
  );
}

/** Enviar. Apunta a la derecha porque el botón va al final de la píldora. */
export function IconEnviar({ size = 18 }: Props) {
  return (
    <svg {...frame(size)}>
      <path d="M3.4 11.9 20.6 4.3l-3.4 16.4-5.1-5.6z" />
      <path d="M12.1 15.1 20.6 4.3" />
    </svg>
  );
}

/** Carnet: la foto a la izquierda y los renglones del dato a la derecha. */
export function IconCarnet({ size = 16 }: Props) {
  return (
    <svg {...frame(size)}>
      <rect x="2.5" y="5" width="19" height="14" rx="2.5" />
      <circle cx="8" cy="11" r="2" />
      <path d="M5 16c.6-1.4 1.7-2 3-2s2.4.6 3 2" />
      <path d="M14.5 10.5h4M14.5 14h3" />
    </svg>
  );
}

/** Lápiz: editar. La punta apunta abajo-izquierda, como se sostiene. */
export function IconLapiz({ size = 16 }: Props) {
  return (
    <svg {...frame(size)}>
      <path d="M16.5 3.9a2 2 0 0 1 2.8 2.8L8.4 17.6 4 19l1.4-4.4z" />
      <path d="M14.5 5.9 17.5 8.9" />
    </svg>
  );
}
